/**
 * `{placeholder}` resolution for template text.
 *
 * Syntax: `{ns.key}` or `{key:format}` (only `date` takes a format, e.g.
 * `{date:DD/MM/YYYY}`). `{{` and `}}` write literal braces. Unknown keys are
 * reported in `missing` and rendered per `missing` option (the editor shows a
 * marker, the PDF leaves them empty).
 *
 * Values are a flat `dotted.key → string` map built by
 * {@link placeholderValues}; resolution itself is pure and synchronous.
 */

import type { PrintFieldDef } from './spec';
import type { SheetRevision } from './sheet';
import { formatLength, formatScale, type PrintLengthUnit } from './units';

export interface PlaceholderDescriptor {
  key: string;
  label: string;
}

/** Everything the editor's placeholder picker offers (fields are added per template). */
export const PRINT_PLACEHOLDERS: readonly PlaceholderDescriptor[] = [
  { key: 'project.name', label: 'Project name' },
  { key: 'project.number', label: 'Project number' },
  { key: 'project.address', label: 'Project address' },
  { key: 'project.client', label: 'Client' },
  { key: 'floor.name', label: 'Floor name' },
  { key: 'floor.elevation', label: 'Floor elevation' },
  { key: 'org.name', label: 'Organisation name' },
  { key: 'org.address', label: 'Organisation address' },
  { key: 'org.phone', label: 'Organisation phone' },
  { key: 'org.email', label: 'Organisation email' },
  { key: 'org.website', label: 'Organisation website' },
  { key: 'org.vat', label: 'Organisation VAT number' },
  { key: 'sheet.name', label: 'Sheet name' },
  { key: 'sheet.number', label: 'Sheet number' },
  { key: 'sheet.rev', label: 'Latest revision' },
  { key: 'page.number', label: 'Page number' },
  { key: 'page.count', label: 'Page count' },
  { key: 'scale', label: 'Scale of the first viewport' },
  { key: 'date', label: 'Export date' },
  { key: 'user.name', label: 'Exported by' },
];

export interface PlaceholderSources {
  project?: { name: string; number?: string; address?: string; client?: string };
  floor?: { name: string; elevation?: number } | null;
  org?: { name: string; address?: string; phone?: string; email?: string; website?: string; vat?: string };
  sheet?: { name: string; number?: string; rev?: string };
  page?: { number: number; count: number };
  user?: { name: string };
  /** Viewports on the page in paint order, by element id; the first one is `{scale}`. */
  viewportScales?: { elementId: string; scaleDen: number }[];
  /** Resolved field values (see {@link resolveFieldValues}). */
  fields?: Readonly<Record<string, string>>;
  /** The row of a revision table being filled. */
  revision?: SheetRevision;
  /** The user's length unit for printed values (default meters). */
  units?: PrintLengthUnit;
}

export type PlaceholderValues = Readonly<Record<string, string>>;

function put(out: Record<string, string>, prefix: string, record: object | null | undefined): void {
  if (!record) return;
  for (const [key, value] of Object.entries(record)) {
    if (value === undefined || value === null) continue;
    out[`${prefix}.${key}`] = String(value);
  }
}

export function placeholderValues(sources: PlaceholderSources): PlaceholderValues {
  const out: Record<string, string> = {};
  put(out, 'project', sources.project);
  if (sources.floor) {
    out['floor.name'] = sources.floor.name;
    if (sources.floor.elevation !== undefined) out['floor.elevation'] = formatLength(sources.floor.elevation, sources.units);
  }
  put(out, 'org', sources.org);
  put(out, 'sheet', sources.sheet);
  put(out, 'page', sources.page);
  put(out, 'user', sources.user);
  put(out, 'field', sources.fields);
  put(out, 'rev', sources.revision);
  const scales = sources.viewportScales ?? [];
  if (scales.length > 0) out['scale'] = formatScale(scales[0].scaleDen);
  for (const { elementId, scaleDen } of scales) out[`viewport.${elementId}.scale`] = formatScale(scaleDen);
  return out;
}

// ─── Dates ──────────────────────────────────────────────────────────────

export const DEFAULT_DATE_FORMAT = 'YYYY-MM-DD';

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

/**
 * Formats with tokens `YYYY`, `YY`, `MMMM` (June), `MMM` (Jun), `MM` (06),
 * `DD` (05), `D` (5), `HH`, `mm` (local time). Month names are English.
 */
export function formatPrintDate(date: Date, format: string = DEFAULT_DATE_FORMAT): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return format.replace(/YYYY|YY|MMMM|MMM|MM|DD|D|HH|mm/g, (token) => {
    switch (token) {
      case 'YYYY':
        return String(date.getFullYear());
      case 'YY':
        return pad(date.getFullYear() % 100);
      case 'MMMM':
        return MONTH_NAMES[date.getMonth()];
      case 'MMM':
        return MONTH_NAMES[date.getMonth()].slice(0, 3);
      case 'MM':
        return pad(date.getMonth() + 1);
      case 'DD':
        return pad(date.getDate());
      case 'D':
        return String(date.getDate());
      case 'HH':
        return pad(date.getHours());
      default:
        return pad(date.getMinutes());
    }
  });
}

/** The date formats the editor offers for `{date:…}` (any other format string works too). */
export const PRINT_DATE_FORMATS: readonly string[] = ['YYYY-MM-DD', 'DD/MM/YYYY', 'DD-MM-YYYY', 'DD.MM.YYYY', 'MM/DD/YYYY', 'D MMMM YYYY', 'MMMM D, YYYY', 'MMMM YYYY', 'MMM YYYY'];

// ─── Resolution ─────────────────────────────────────────────────────────

/** Literal text as template text: braces doubled so they never read as placeholders. */
export function escapePlaceholderText(text: string): string {
  return text.replace(/[{}]/g, (brace) => brace + brace);
}

const KEY_PATTERN = /^[A-Za-z][A-Za-z0-9_.-]*$/;

export type PlaceholderToken = { kind: 'literal'; text: string } | { kind: 'placeholder'; key: string; format: string | undefined };

/** Splits template text into literals and placeholders; malformed braces stay literal. */
export function tokenizePlaceholders(template: string): PlaceholderToken[] {
  const tokens: PlaceholderToken[] = [];
  let literal = '';
  let i = 0;
  while (i < template.length) {
    const ch = template[i];
    if ((ch === '{' || ch === '}') && template[i + 1] === ch) {
      literal += ch;
      i += 2;
      continue;
    }
    if (ch === '{') {
      const close = template.indexOf('}', i + 1);
      const inner = close === -1 ? '' : template.slice(i + 1, close);
      const colon = inner.indexOf(':');
      const key = colon === -1 ? inner : inner.slice(0, colon);
      if (close !== -1 && KEY_PATTERN.test(key)) {
        if (literal) tokens.push({ kind: 'literal', text: literal });
        literal = '';
        tokens.push({ kind: 'placeholder', key, format: colon === -1 ? undefined : inner.slice(colon + 1) });
        i = close + 1;
        continue;
      }
    }
    literal += ch;
    i += 1;
  }
  if (literal) tokens.push({ kind: 'literal', text: literal });
  return tokens;
}

export interface ResolveOptions {
  /** Export timestamp for `{date}`. */
  date: Date;
  /** How unknown keys render: `empty` (export) or `marker` (`‹key›`, editor preview). */
  missing: 'empty' | 'marker';
}

export interface ResolvedText {
  text: string;
  /** Unknown keys, in order of first appearance. */
  missing: string[];
}

export function resolvePlaceholders(template: string, values: PlaceholderValues, options: ResolveOptions): ResolvedText {
  let text = '';
  const missing: string[] = [];
  for (const token of tokenizePlaceholders(template)) {
    if (token.kind === 'literal') {
      text += token.text;
      continue;
    }
    const value = token.key === 'date' ? formatPrintDate(options.date, token.format) : values[token.key];
    if (value !== undefined) {
      text += value;
      continue;
    }
    if (!missing.includes(token.key)) missing.push(token.key);
    if (options.missing === 'marker') text += `‹${token.key}›`;
  }
  return { text, missing };
}

/** Distinct placeholder keys a template string references, in order of appearance. */
export function placeholderKeysOf(template: string): string[] {
  const keys: string[] = [];
  for (const token of tokenizePlaceholders(template)) {
    if (token.kind === 'placeholder' && !keys.includes(token.key)) keys.push(token.key);
  }
  return keys;
}

/**
 * Field values for a sheet: the sheet's value when non-empty, otherwise the
 * field default resolved against `baseValues` (which must not contain
 * `field.*` keys, so defaults cannot reference each other).
 */
export function resolveFieldValues(fields: readonly PrintFieldDef[], sheetValues: Readonly<Record<string, string>>, baseValues: PlaceholderValues, options: ResolveOptions): Record<string, string> {
  const out: Record<string, string> = {};
  for (const field of fields) {
    const own = sheetValues[field.key];
    out[field.key] = own !== undefined && own !== '' ? own : resolvePlaceholders(field.defaultValue, baseValues, options).text;
  }
  return out;
}
